import React, { useEffect, useMemo, useRef, useState } from 'react';
import './DtaiDqiDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://genaiapi.enterprise360uat.genpact.com/wd-tfa/doDqi';
const PAGE_SIZE = 20;
const FILTER_FIRST_PAGE_SIZE = 50;
const FILTER_NEXT_PAGE_SIZE = 10;
const SHARED_DQI_FILTERS_KEY = 'mosaic.sharedDqiFilters';
const SHARED_DQI_FILTERS_VERSION = 2;

const DTAI_ENDPOINTS = {
  filters: `${API_BASE_URL}/dashboard/dtai-dqi/filters`,
  accounts: `${API_BASE_URL}/dashboard/dtai-dqi/accounts`,
  summary: `${API_BASE_URL}/dashboard/dtai-dqi/summary`,
  performance: `${API_BASE_URL}/dashboard/dtai-dqi/performance`,
  capability: `${API_BASE_URL}/dashboard/dtai-dqi/capability`,
  clientFeedback: `${API_BASE_URL}/dashboard/dtai-dqi/client-feedback`,
  responsiveness: `${API_BASE_URL}/dashboard/dtai-dqi/responsiveness`,
};

const DO_ENDPOINTS = {
  summary: `${API_BASE_URL}/dashboard/do-dqi/summary`,
};

const defaultFilters = {
  unitId: '',
  parentAccountId: '',
  custAcctId: '',
  custAcctLabels: '',
  srvcLnId: '',
  rgnId: '',
  revenue: '',
  year: '2025',
  period: '20250430',
  headcount: '',
};

const serviceLineOptions = [
  { label: 'All', value: '' },
  { label: 'Capital Markets', value: 'Capital Markets' },
  { label: 'Claims', value: 'Claims' },
  { label: 'Commercial Services', value: 'Commercial Services' },
  { label: 'Customer Care', value: '121' },
  { label: 'F&A Multi-tower', value: 'F&A Multi-tower' },
  { label: 'Finance - Accounts Payable', value: '85' },
  { label: 'Finance - Invoice To Cash', value: '94' },
];

const deliveryRegionOptions = [
  { label: 'All', value: '' },
  { label: 'NAM', value: '1' },
  { label: 'AUSTRALIA', value: '2' },
  { label: 'CHINA', value: '3' },
  { label: 'EMEA', value: '4' },
  { label: 'INDIA', value: '5' },
  { label: 'PHILIPPINES', value: '6' },
  { label: 'LATAM', value: '7' },
];

const headcountOptions = [
  { label: 'All', value: '' },
  { label: '>=100', value: '>=100' },
  { label: '>=50', value: '>=50' },
];

const revenueOptions = [
  { label: 'All', value: '' },
  { label: '(Blank)', value: '(Blank)' },
  { label: '<5M', value: '<5M' },
  { label: '>10M', value: '>10M' },
  { label: '5M-10M', value: '5M-10M' },
];

const periodOptions = [
  { label: 'All', value: '' },
  { label: 'JANUARY', value: '20250131' },
  { label: 'FEBRUARY', value: '20250228' },
  { label: 'MARCH', value: '20250331' },
  { label: 'APRIL', value: '20250430' },
  { label: 'MAY', value: '20250531' },
  { label: 'JUNE', value: '20250630' },
  { label: 'JULY', value: '20250731' },
  { label: 'AUGUST', value: '20250831' },
  { label: 'SEPTEMBER', value: '20250930' },
  { label: 'OCTOBER', value: '20251031' },
  { label: 'NOVEMBER', value: '20251130' },
  { label: 'DECEMBER', value: '20251231' },
];

const reportingYearOptions = [
  { label: '2024', value: '2024' },
  { label: '2025', value: '2025' },
  { label: '2026', value: '2026' },
];

const readSharedFilters = (fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(SHARED_DQI_FILTERS_KEY);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored);
    const migratedFilters = { ...fallback, ...parsed };
    if (parsed._version !== SHARED_DQI_FILTERS_VERSION && parsed.headcount === '>=100') {
      migratedFilters.headcount = '';
    }
    if (migratedFilters.srvcLnId === 'Finance - Accounting') {
      migratedFilters.srvcLnId = '94';
    }
    delete migratedFilters._version;
    return migratedFilters;
  } catch {
    return fallback;
  }
};

const writeSharedFilters = (filters) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SHARED_DQI_FILTERS_KEY, JSON.stringify({
    ...filters,
    _version: SHARED_DQI_FILTERS_VERSION,
  }));
};

const normalizeOptionKey = (value) => String(value || '').trim().toLowerCase();

const uniqueOptions = (options, keySelector = (option) => option.value) => {
  const unique = [];
  const seen = new Set();

  options.forEach((option) => {
    const key = normalizeOptionKey(keySelector(option));
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(option);
  });

  return unique;
};

const mergeOptions = (current, next) => uniqueOptions([...current, ...next], (option) => option.value || option.label);
const getSelectedValues = (value) => String(value || '').split(',').filter(Boolean);
const getSelectedLabels = (value) => String(value || '').split('||').filter(Boolean);

const getRevenueFilterValues = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter((item) => item && item.toLowerCase() !== 'all');

const isBlankRevenueValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === '(blank)' || normalized === 'blank' || normalized === '__blank__';
};

const shouldShowNoDataForRevenue = (filters) => {
  const revenueValues = getRevenueFilterValues(filters.revenue);
  return revenueValues.length > 0 && !revenueValues.some(isBlankRevenueValue);
};

const emptySummary = {
  score: null,
  scoreLabel: '--',
  score_label: '--',
  variation: null,
  variationLabel: null,
  variation_label: null,
  direction: 'up',
};

const blankDetailScores = (detail) => ({
  ...detail,
  data: (detail?.data || []).map((row) => ({
    ...row,
    weightedScore: null,
    weightedScoreLabel: '--',
  })),
  overall: detail?.overall
    ? { ...detail.overall, weightedScore: null, weightedScoreLabel: '--' }
    : null,
});

const formatText = (value) => (value == null || value === '' ? '--' : value);

const formatNumber = (value, digits = 1) => {
  if (value === null || value === undefined || value === '') return '--';
  const number = Number(value);
  if (!Number.isFinite(number)) return '--';
  return number.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const formatPercent = (value) => {
  const formatted = formatNumber(value);
  return formatted === '--' ? formatted : `${formatted}%`;
};

const buildUrl = (endpoint, filters, extra = {}) => {
  const params = new URLSearchParams();
  if (filters.unitId) params.set('unitId', filters.unitId);
  if (filters.parentAccountId) params.set('parentAccountId', filters.parentAccountId);
  if (filters.custAcctId) params.set('custAcctId', filters.custAcctId);
  if (filters.srvcLnId) params.set('srvcLnId', filters.srvcLnId);
  if (filters.rgnId) params.set('rgnId', filters.rgnId);
  if (filters.revenue) params.set('revenue', filters.revenue);
  if (filters.year) params.set('year', String(filters.year).split(',')[0]);
  if (filters.period) params.set('period', getMonthEndPeriod(filters.period, filters.year));
  if (filters.headcount) params.set('headcount', filters.headcount);

  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });

  const query = params.toString();
  return query ? `${endpoint}?${query}` : endpoint;
};

const getMonthEndPeriod = (period, year) => {
  const value = String(period || '').split(',')[0];
  const selectedYear = String(year || value.slice(0, 4)).split(',')[0];
  if (!/^\d{8}$/.test(value) || !/^\d{4}$/.test(selectedYear)) return value;
  const month = value.slice(4, 6);
  const lastDay = new Date(Number(selectedYear), Number(month), 0).getDate();
  return `${selectedYear}${month}${String(lastDay).padStart(2, '0')}`;
};

const buildDoSummaryUrl = (filters) => {
  const params = new URLSearchParams();
  if (filters.unitId) params.set('unitId', filters.unitId);
  if (filters.custAcctId) params.set('custAcctId', filters.custAcctId);
  if (filters.srvcLnId) params.set('srvcLnId', filters.srvcLnId);
  if (filters.rgnId) params.set('rgnId', filters.rgnId);
  if (filters.revenue) params.set('revenue', filters.revenue);
  if (filters.year) params.set('year', String(filters.year).split(',')[0]);
  if (filters.period) params.set('period', getMonthEndPeriod(filters.period, filters.year));
  if (filters.headcount) params.set('headcount', filters.headcount);
  const query = params.toString();
  return query ? `${DO_ENDPOINTS.summary}?${query}` : DO_ENDPOINTS.summary;
};

const getFilterOptionsRequestFilters = (filters, type = '') => ({
  ...filters,
  custAcctId: type === 'accounts' ? '' : filters.custAcctId,
});

const normalizeUnits = (payload) => [
  { label: 'All', value: '' },
  ...(payload?.filters?.units || [])
    .map((unit) => unit.unitId == null ? null : { label: String(unit.unitName), value: String(unit.unitId) })
    .filter(Boolean),
];

const normalizeParents = (payload) => [
  { label: 'All', value: '' },
  ...uniqueOptions(
    (payload?.filters?.parentAccounts || [])
      .map((row) => row.parentAccountId == null ? null : {
        label: String(row.parentAccountName),
        value: String(row.parentAccountId),
      })
      .filter(Boolean),
    (option) => option.value
  ),
];

const normalizeAccounts = (payload) => [
  { label: 'All', value: '' },
  ...uniqueOptions(
    (payload?.filters?.accounts || [])
      .map((row) => {
        const value = row.customerId ?? row.customer_id ?? row.custAcctId ?? row.cust_acct_id ?? row.accountUid ?? row.accountId ?? row.value;
        const label = row.customerName ?? row.customer_name ?? row.custAcctName ?? row.accountName ?? row.name ?? row.label ?? value;

        return value == null || value === ''
          ? null
          : { label: String(label), value: String(value) };
      })
      .filter(Boolean),
    (option) => option.value
  ),
];

const normalizeServiceLines = (payload) => {
  const options = (payload?.filters?.serviceLines || [])
    .map((row) => {
      const value = row.serviceLineId ?? row.serviceLineUid ?? row.srvcLnId ?? row.srvc_ln_id;
      const label = row.serviceLineName ?? row.serviceLine ?? row.service_line ?? value;
      return value && label ? { label: String(label), value: String(value) } : null;
    })
    .filter(Boolean);

  return options.length ? [{ label: 'All', value: '' }, ...uniqueOptions(options, (option) => option.label)] : serviceLineOptions;
};

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onSearchChange,
  isSearching = false,
  fallbackLabels = [],
  multiple = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const selectedValues = multiple ? getSelectedValues(value) : getSelectedValues(value).slice(0, 1);
  const selectableOptions = options.filter((option) => option.value !== '');
  const trimmedSearchText = searchText.trim().toLowerCase();
  const filteredOptions = onSearchChange
    ? selectableOptions
    : selectableOptions.filter((option) => option.label.toLowerCase().includes(trimmedSearchText));
  const selectAllOptions = trimmedSearchText ? filteredOptions : selectableOptions;
  const selectedLabels = selectedValues
    .map((selectedValue, index) => options.find((option) => option.value === selectedValue)?.label || fallbackLabels[index] || 'Selected')
    .filter(Boolean);
  const displayLabel = selectedLabels.length === 0
    ? 'All'
    : selectedLabels.length === 1
      ? selectedLabels[0]
      : 'Multiple selected';

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !onSearchChange) return undefined;

    const timeoutId = window.setTimeout(() => {
      onSearchChange(searchText.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, searchText]);

  const handleMenuScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 24;
    const isScrollingDown = scrollTop > lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;

    if (isNearBottom && isScrollingDown && hasMore && !isLoadingMore) {
      onLoadMore?.();
    }
  };

  const updateSelectedValues = (nextValues) => {
    const nextSelectedOptions = nextValues.map((nextValue) => {
      const option = options.find((candidate) => candidate.value === nextValue);
      return option || { value: nextValue, label: fallbackLabels[selectedValues.indexOf(nextValue)] || '' };
    });
    onChange(multiple ? nextValues.join(',') : (nextValues[0] || ''), nextSelectedOptions);
  };

  const toggleOption = (optionValue) => {
    if (!optionValue) {
      updateSelectedValues([]);
      return;
    }

    if (!multiple) {
      updateSelectedValues([optionValue]);
      setIsOpen(false);
      return;
    }

    updateSelectedValues(
      selectedValues.includes(optionValue)
        ? selectedValues.filter((selectedValue) => selectedValue !== optionValue)
        : [...selectedValues, optionValue]
    );
  };

  const toggleSelectAll = () => {
    const selectAllValues = selectAllOptions.map((option) => option.value);
    const allVisibleSelected = selectAllValues.length > 0 && selectAllValues.every((optionValue) => selectedValues.includes(optionValue));

    updateSelectedValues(
      allVisibleSelected
        ? []
        : Array.from(new Set([...selectedValues, ...selectAllValues]))
    );
  };

  return (
    <label className="dtai-filter-field">
      <span>{label}</span>
      <div className="dtai-filter-dropdown" ref={dropdownRef}>
        <button
          type="button"
          className="dtai-select-trigger"
          onClick={() => setIsOpen((current) => !current)}
          title={selectedLabels.join(', ') || 'All'}
        >
          <span>{displayLabel}</span>
          <span className="filter-dropdown-caret" aria-hidden="true" />
        </button>
        {isOpen && (
          <div className="dtai-dropdown" aria-label={label} onScroll={handleMenuScroll}>
            <div className="dtai-dropdown-search">
              <span aria-hidden="true" />
              <input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search" />
            </div>
            {multiple && (
              <button
                type="button"
                className="dtai-dropdown-option"
                onClick={toggleSelectAll}
                title="Select all"
              >
                <input type="checkbox" checked={selectAllOptions.length > 0 && selectAllOptions.every((option) => selectedValues.includes(option.value))} readOnly />
                <span>Select all</span>
              </button>
            )}
            {isSearching && (
              <div className="dtai-dropdown-empty">Searching...</div>
            )}
            {!isSearching && trimmedSearchText && !filteredOptions.length && (
              <div className="dtai-dropdown-empty">No results found</div>
            )}
            {filteredOptions.map((option) => (
              <button
                type="button"
                key={`${option.value}-${option.label}`}
                className={`dtai-dropdown-option ${option.value && selectedValues.includes(option.value) ? 'selected' : ''}`}
                onClick={() => toggleOption(option.value)}
                title={option.label}
              >
                <input type={multiple ? 'checkbox' : 'radio'} checked={option.value === '' ? selectedValues.length === 0 : selectedValues.includes(option.value)} readOnly />
                <span>{option.label}</span>
              </button>
            ))}
            {hasMore && (
              <div className="dtai-dropdown-more">
                {isLoadingMore ? 'Loading...' : 'Scroll for more'}
              </div>
            )}
          </div>
        )}
      </div>
    </label>
  );
};

const getSummaryScoreLabel = (summary) => summary?.score_label || summary?.scoreLabel || '--';
const getSummaryVariationLabel = (summary) => summary?.variation_label || summary?.variationLabel || null;
const getVariationArrow = (direction) => String.fromCharCode(direction === 'down' ? 8595 : 8593);

const ScoreCards = ({ summary, doSummary }) => (
  <div className="score-card-row dtai-score-card-row">
    <button type="button" className="score-card dtai-do-score-card score-card-button" onClick={() => { window.location.href = '/'; }}>
      <h3>DO DQI</h3>
      <div className="score-line">
        <strong>{getSummaryScoreLabel(doSummary)}</strong>
      </div>
    </button>
    {getSummaryVariationLabel(doSummary) && (
      <span className={`variation dtai-do-live-variation ${doSummary.direction === 'down' ? 'down' : 'up'}`}>
        {getVariationArrow(doSummary.direction)} {getSummaryVariationLabel(doSummary)}
      </span>
    )}
    <button
      type="button"
      className="score-card dtai-primary-score-card score-card-button"
      onClick={() => document.getElementById('dtai-score-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
    >
      <h3>DTAI DQI</h3>
      <div className="score-line">
        <strong>{summary?.scoreLabel || '--'}</strong>
        {summary?.variationLabel && (
          <span className={`variation ${summary.direction === 'down' ? 'down' : 'up'}`}>
            {getVariationArrow(summary.direction)} {summary.variationLabel}
          </span>
        )}
      </div>
    </button>
  </div>
);

const AccountsTable = ({ rows, meta, isLoading, pageOffset, setPageOffset }) => {
  const total = Number(meta?.totalRecords) || rows.length;
  const currentPage = Math.floor(pageOffset / PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const nextOffset = meta?.nextOffset;
  const lastOffset = Math.max(0, Math.floor(Math.max(0, total - 1) / PAGE_SIZE) * PAGE_SIZE);

  return (
    <div className="do-panel" id="dtai-score-section">
      <h2>DTAI DQI Score</h2>
      <div className="do-table-scroll account-table-shell">
        <table className="do-table">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Delivery Quality Index</th>
              <th>Performance</th>
              <th>Capability</th>
              <th>Client Feedback</th>
              <th>Responsiveness</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="6" className="empty-cell">Loading accounts...</td></tr>
            ) : rows.length ? rows.map((row) => (
              <tr key={`${row.period}-${row.customerId}`}>
                <td>{row.customerName}</td>
                <td className={Number(row.dqiScore) < 50 ? 'low-score-cell' : 'good-score-cell'}>{row.dqiLabel || formatPercent(row.dqiScore)}</td>
                <td>{row.performanceLabel || formatPercent(row.performance)}</td>
                <td>{row.capabilityLabel || formatPercent(row.capability)}</td>
                <td>{row.clientFeedbackLabel || formatPercent(row.clientFeedback)}</td>
                <td>{row.responsivenessLabel || formatPercent(row.responsiveness)}</td>
              </tr>
            )) : (
              <tr><td colSpan="6" className="empty-cell">No accounts returned for the selected period.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="account-pager">
        <span>{rows.length ? `${pageOffset + 1}-${pageOffset + rows.length} of ${total}` : '0 accounts'}</span>
        <button type="button" onClick={() => setPageOffset(0)} disabled={pageOffset === 0 || isLoading}>First</button>
        <button type="button" onClick={() => setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))} disabled={pageOffset === 0 || isLoading}>Previous</button>
        <span>{currentPage + 1} / {pageCount}</span>
        <button type="button" onClick={() => setPageOffset(Number(nextOffset))} disabled={nextOffset == null || Number(nextOffset) <= pageOffset || isLoading}>Next</button>
        <button type="button" onClick={() => setPageOffset(lastOffset)} disabled={pageOffset >= lastOffset || isLoading}>Last</button>
      </div>
    </div>
  );
};

const DetailTable = ({ detail }) => {
  const rows = detail?.data || [];

  return (
    <div className="do-panel selected-account-panel">
      <div className="selected-account-header">
        <div>
          <h3>Your selected accounts</h3>
          <h2>{detail?.title || 'DTAI Detail'}</h2>
        </div>
        <button type="button">View More Details</button>
      </div>
      <div className="do-table-scroll">
        <table className="do-table dtai-score-table">
          <thead>
            <tr>
              <th>Sub Parameter</th>
              <th>Weighted Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row) => (
              <tr key={row.subParameter}>
                <td>{formatText(row.subParameter)}</td>
                <td>{row.weightedScoreLabel || formatPercent(row.weightedScore)}</td>
              </tr>
            )) : (
              <tr><td colSpan="2" className="empty-cell">No detail rows returned.</td></tr>
            )}
            <tr className="overall-row">
              <td>{detail?.overall?.subParameter || 'Overall Weighted Score'}</td>
              <td>{detail?.overall?.weightedScoreLabel || formatPercent(detail?.overall?.weightedScore)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DtaiDqiDashboard = () => {
  const [filters, setFilters] = useState(() => readSharedFilters(defaultFilters));
  const [appliedFilters, setAppliedFilters] = useState(() => readSharedFilters(defaultFilters));
  const [unitOptions, setUnitOptions] = useState([{ label: 'All', value: '' }]);
  const [parentOptions, setParentOptions] = useState([{ label: 'All', value: '' }]);
  const [accountOptions, setAccountOptions] = useState([{ label: 'All', value: '' }]);
  const [serviceLineFilterOptions, setServiceLineFilterOptions] = useState(serviceLineOptions);
  const [pagination, setPagination] = useState({ parentAccounts: { nextOffset: null }, accounts: { nextOffset: null } });
  const [loadingMore, setLoadingMore] = useState(null);
  const [searchingFilter, setSearchingFilter] = useState(null);
  const [filterSearchText, setFilterSearchText] = useState({ units: '', parentAccounts: '', accounts: '' });
  const [summary, setSummary] = useState(null);
  const [doSummary, setDoSummary] = useState(null);
  const [details, setDetails] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountsMeta, setAccountsMeta] = useState(null);
  const [pageOffset, setPageOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccountsLoading, setIsAccountsLoading] = useState(true);
  const [error, setError] = useState(null);
  const latestFiltersRef = useRef(filters);

  const summaryUrl = useMemo(() => buildUrl(DTAI_ENDPOINTS.summary, appliedFilters), [appliedFilters]);
  const doSummaryUrl = useMemo(() => buildDoSummaryUrl(appliedFilters), [appliedFilters]);
  const detailUrls = useMemo(() => [
    buildUrl(DTAI_ENDPOINTS.performance, appliedFilters),
    buildUrl(DTAI_ENDPOINTS.capability, appliedFilters),
    buildUrl(DTAI_ENDPOINTS.clientFeedback, appliedFilters),
    buildUrl(DTAI_ENDPOINTS.responsiveness, appliedFilters),
  ], [appliedFilters]);
  const accountsUrl = useMemo(() => buildUrl(DTAI_ENDPOINTS.accounts, appliedFilters, { limit: PAGE_SIZE, offset: pageOffset }), [appliedFilters, pageOffset]);
  const revenueNoData = useMemo(() => shouldShowNoDataForRevenue(appliedFilters), [appliedFilters]);

  useEffect(() => {
    latestFiltersRef.current = filters;
    writeSharedFilters(filters);
  }, [filters]);

  const loadFilters = async (nextFilters = filters, extra = {}) => {
    const response = await fetch(buildUrl(DTAI_ENDPOINTS.filters, getFilterOptionsRequestFilters(nextFilters, extra.type), {
      parentAccountLimit: FILTER_FIRST_PAGE_SIZE,
      accountLimit: FILTER_FIRST_PAGE_SIZE,
      ...extra,
      type: undefined,
    }));
    if (!response.ok) throw new Error(`DTAI filters API returned ${response.status}`);
    const payload = await response.json();
    setUnitOptions(normalizeUnits(payload));
    setServiceLineFilterOptions(normalizeServiceLines(payload));
    setParentOptions(normalizeParents(payload));
    setAccountOptions(normalizeAccounts(payload));
    setPagination(payload.pagination || { parentAccounts: { nextOffset: null }, accounts: { nextOffset: null } });
    return payload;
  };

  useEffect(() => {
    let ignore = false;
    loadFilters(filters, { type: 'accounts' }).catch((loadError) => {
      if (!ignore) setError(loadError.message || 'Failed to load DTAI filters');
    });
    return () => { ignore = true; };
  }, [filters.custAcctId, filters.headcount, filters.parentAccountId, filters.period, filters.revenue, filters.rgnId, filters.srvcLnId, filters.unitId, filters.year]);

  useEffect(() => {
    let ignore = false;

    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [nextSummary, nextDoSummary, ...nextDetails] = await Promise.all([
          fetch(summaryUrl).then((response) => {
            if (!response.ok) throw new Error(`DTAI summary API returned ${response.status}`);
            return response.json();
          }),
          fetch(doSummaryUrl).then((response) => {
            if (!response.ok) throw new Error(`DO DQI summary API returned ${response.status}`);
            return response.json();
          }),
          ...detailUrls.map((url) => fetch(url).then((response) => {
            if (!response.ok) throw new Error(`DTAI detail API returned ${response.status}`);
            return response.json();
          })),
        ]);
        if (ignore) return;
        if (revenueNoData) {
          setSummary(emptySummary);
          setDoSummary(emptySummary);
          setDetails(nextDetails.map(blankDetailScores));
        } else {
          setSummary(nextSummary);
          setDoSummary(nextDoSummary);
          setDetails(nextDetails);
        }
      } catch (loadError) {
        if (!ignore) setError(loadError.message || 'Failed to load DTAI data');
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    loadData();
    return () => { ignore = true; };
  }, [doSummaryUrl, revenueNoData, summaryUrl, detailUrls]);

  useEffect(() => {
    let ignore = false;

    const loadAccounts = async () => {
      try {
        setIsAccountsLoading(true);
        if (revenueNoData) {
          if (!ignore) {
            setAccounts([]);
            setAccountsMeta({ totalRecords: 0, nextOffset: null });
          }
          return;
        }
        const response = await fetch(accountsUrl);
        if (!response.ok) throw new Error(`DTAI accounts API returned ${response.status}`);
        const payload = await response.json();
        if (ignore) return;
        setAccounts(payload.data || []);
        setAccountsMeta(payload);
      } catch (loadError) {
        if (!ignore) setError(loadError.message || 'Failed to load DTAI accounts');
      } finally {
        if (!ignore) setIsAccountsLoading(false);
      }
    };

    loadAccounts();
    return () => { ignore = true; };
  }, [accountsUrl, revenueNoData]);

  useEffect(() => {
    const selectedAccountIds = new Set(getSelectedValues(filters.custAcctId));
    if (!selectedAccountIds.size || !accounts.length) return;

    const selectedAccountOptions = accounts
      .map((account) => {
        const value = account.customerId ?? account.customer_id ?? account.custAcctId ?? account.accountId;
        const label = account.customerName ?? account.customer_name ?? account.custAcctName ?? account.accountName ?? value;

        return value == null
          ? null
          : { label: String(label), value: String(value) };
      })
      .filter((option) => option && selectedAccountIds.has(option.value));

    if (selectedAccountOptions.length) {
      setAccountOptions((current) => mergeOptions(current, selectedAccountOptions));
    }
  }, [accounts, filters.custAcctId]);

  useEffect(() => {
    const selectedValues = getSelectedValues(filters.custAcctId);
    if (!selectedValues.length) return;

    const selectedLabels = selectedValues
      .map((selectedValue) => accountOptions.find((option) => option.value === selectedValue)?.label)
      .filter(Boolean);

    if (selectedLabels.length === selectedValues.length && selectedLabels.join('||') !== filters.custAcctLabels) {
      setFilters((current) => ({ ...current, custAcctLabels: selectedLabels.join('||') }));
    }
  }, [accountOptions, filters.custAcctId, filters.custAcctLabels]);

  const updateFilter = (key, value) => {
    setFilters((current) => {
      const next = { ...current, [key]: value };
      if (key === 'year') {
        next.custAcctId = '';
        next.custAcctLabels = '';
      }
      return next;
    });
  };

  const updateAccountFilter = (value, selectedOptions = []) => {
    setFilters((current) => ({
      ...current,
      custAcctId: value,
      custAcctLabels: selectedOptions.map((option) => option.label).filter(Boolean).join('||'),
    }));
  };

  const handleUnitChange = (unitId) => {
    setFilterSearchText((current) => ({ ...current, parentAccounts: '', accounts: '' }));
    setParentOptions([{ label: 'All', value: '' }]);
    setAccountOptions([{ label: 'All', value: '' }]);
    setFilters((current) => ({ ...current, unitId, parentAccountId: '', custAcctId: '', custAcctLabels: '' }));
  };

  const handleParentChange = (parentAccountId) => {
    setFilterSearchText((current) => ({ ...current, accounts: '' }));
    setAccountOptions([{ label: 'All', value: '' }]);
    setFilters((current) => ({ ...current, parentAccountId, custAcctId: '', custAcctLabels: '' }));
  };

  const submitFilters = () => {
    setPageOffset(0);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const searchFilterOptions = async (type, searchText) => {
    const trimmedSearchText = searchText.trim();
    const requestFilters = { ...latestFiltersRef.current };

    setFilterSearchText((current) => ({ ...current, [type]: trimmedSearchText }));

    try {
      setSearchingFilter(type);
      if (!trimmedSearchText) {
        const response = await fetch(buildUrl(DTAI_ENDPOINTS.filters, getFilterOptionsRequestFilters(requestFilters, type), {
          parentAccountLimit: type === 'parentAccounts' ? FILTER_FIRST_PAGE_SIZE : 1,
          parentAccountOffset: 0,
          accountLimit: type === 'accounts' ? FILTER_FIRST_PAGE_SIZE : 1,
          accountOffset: 0,
        }));
        if (!response.ok) throw new Error(`DTAI filters API returned ${response.status}`);
        const payload = await response.json();
        if (type === 'units') setUnitOptions(normalizeUnits(payload));
        if (type === 'parentAccounts') setParentOptions(normalizeParents(payload));
        if (type === 'accounts') setAccountOptions(normalizeAccounts(payload));
        setPagination((current) => ({
          ...current,
          ...(type === 'parentAccounts' ? { parentAccounts: payload.pagination?.parentAccounts || { nextOffset: null } } : {}),
          ...(type === 'accounts' ? { accounts: payload.pagination?.accounts || { nextOffset: null } } : {}),
        }));
        return;
      }

      const response = await fetch(buildUrl(DTAI_ENDPOINTS.filters, getFilterOptionsRequestFilters(requestFilters, type), {
        unitSearch: type === 'units' ? trimmedSearchText : undefined,
        parentAccountSearch: type === 'parentAccounts' ? trimmedSearchText : undefined,
        accountSearch: type === 'accounts' ? trimmedSearchText : undefined,
        parentAccountLimit: type === 'parentAccounts' ? FILTER_FIRST_PAGE_SIZE : 1,
        parentAccountOffset: 0,
        accountLimit: type === 'accounts' ? FILTER_FIRST_PAGE_SIZE : 1,
        accountOffset: 0,
      }));
      if (!response.ok) throw new Error(`DTAI filters API returned ${response.status}`);
      const payload = await response.json();

      if (type === 'units') setUnitOptions(normalizeUnits(payload));
      if (type === 'parentAccounts') setParentOptions(normalizeParents(payload));
      if (type === 'accounts') setAccountOptions(normalizeAccounts(payload));
      setPagination((current) => ({
        ...current,
        ...(type === 'parentAccounts' ? { parentAccounts: payload.pagination?.parentAccounts || { nextOffset: null } } : {}),
        ...(type === 'accounts' ? { accounts: payload.pagination?.accounts || { nextOffset: null } } : {}),
      }));
    } catch (loadError) {
      setError(loadError.message || 'Failed to search DTAI filter records');
    } finally {
      setSearchingFilter(null);
    }
  };

  const loadMore = async (type) => {
    const key = type === 'parentAccounts' ? 'parentAccounts' : 'accounts';
    const nextOffset = pagination[key]?.nextOffset;
    if (nextOffset == null || loadingMore) return;

    try {
      setLoadingMore(key);
      const payload = await fetch(buildUrl(DTAI_ENDPOINTS.filters, getFilterOptionsRequestFilters(latestFiltersRef.current, key), {
        parentAccountSearch: key === 'parentAccounts' ? filterSearchText.parentAccounts : undefined,
        accountSearch: key === 'accounts' ? filterSearchText.accounts : undefined,
        parentAccountLimit: key === 'parentAccounts' ? FILTER_NEXT_PAGE_SIZE : 1,
        parentAccountOffset: key === 'parentAccounts' ? nextOffset : 0,
        accountLimit: key === 'accounts' ? FILTER_NEXT_PAGE_SIZE : 1,
        accountOffset: key === 'accounts' ? nextOffset : 0,
      })).then((response) => {
        if (!response.ok) throw new Error(`DTAI filters API returned ${response.status}`);
        return response.json();
      });

      if (key === 'parentAccounts') setParentOptions((current) => mergeOptions(current, normalizeParents(payload)));
      if (key === 'accounts') setAccountOptions((current) => mergeOptions(current, normalizeAccounts(payload)));
      setPagination((current) => ({ ...current, [key]: payload.pagination?.[key] || { nextOffset: null } }));
    } catch (loadError) {
      setError(loadError.message || 'Failed to load more DTAI filter records');
    } finally {
      setLoadingMore(null);
    }
  };

  return (
    <div className="do-dashboard dtai-dashboard">
      <header className="do-topbar">
        <div className="brand">enterprise<span>360</span></div>
        <div className="user-block">
          <span>703432047@genpact.com</span>
          <strong>Full Access</strong>
        </div>
      </header>

      <main className="do-shell">
        <section className="do-intro">
          <p><strong>Delivery Quality Index (DQI)</strong> is a comprehensive measure of quality of delivery health. Refer here: <a href="/">DQI Playbook</a> | <a href="/">FAQ</a></p>
          <div><strong>Last Updated:</strong> 12 August, 2026</div>
        </section>

        <section className="dtai-filter-grid">
          <FilterSelect label="Unit" value={filters.unitId} onChange={handleUnitChange} options={unitOptions} onSearchChange={(searchText) => searchFilterOptions('units', searchText)} isSearching={searchingFilter === 'units'} />
          <FilterSelect label="Parent Account" value={filters.parentAccountId} onChange={handleParentChange} options={parentOptions} hasMore={pagination.parentAccounts?.nextOffset != null} isLoadingMore={loadingMore === 'parentAccounts'} onLoadMore={() => loadMore('parentAccounts')} onSearchChange={(searchText) => searchFilterOptions('parentAccounts', searchText)} isSearching={searchingFilter === 'parentAccounts'} />
          <FilterSelect label="Account" value={filters.custAcctId} onChange={updateAccountFilter} options={accountOptions} fallbackLabels={getSelectedLabels(filters.custAcctLabels)} hasMore={pagination.accounts?.nextOffset != null} isLoadingMore={loadingMore === 'accounts'} onLoadMore={() => loadMore('accounts')} onSearchChange={(searchText) => searchFilterOptions('accounts', searchText)} isSearching={searchingFilter === 'accounts'} />
          <FilterSelect label="Service Line" value={filters.srvcLnId} onChange={(value) => updateFilter('srvcLnId', value)} options={serviceLineFilterOptions} />
          <FilterSelect label="Delivery Region" value={filters.rgnId} onChange={(value) => updateFilter('rgnId', value)} options={deliveryRegionOptions} />
          <FilterSelect label="Revenue" value={filters.revenue} onChange={(value) => updateFilter('revenue', value)} options={revenueOptions} />
          <FilterSelect label="Reporting Year" value={filters.year} onChange={(value) => updateFilter('year', value)} options={reportingYearOptions} multiple={false} />
          <FilterSelect label="Reporting Period" value={filters.period} onChange={(value) => updateFilter('period', value)} options={periodOptions} multiple={false} />
          <FilterSelect label="Headcount" value={filters.headcount} onChange={(value) => updateFilter('headcount', value)} options={headcountOptions} />
          <button type="button" className="submit-button" onClick={submitFilters}>Submit</button>
          <button type="button" className="clear-button" onClick={clearFilters}>Clear Filters</button>
        </section>

        <section className="do-tabs">
          <button type="button" onClick={() => { window.location.href = '/'; }}>DQI SUMMARY</button>
          <button type="button" className="active">DQI DETAIL VIEW</button>
          <div className="do-actions">
            <button type="button" onClick={() => { window.location.href = '/db'; }}>SQL Query</button>
            <button type="button">DQI (with Transformation & Risk Index)</button>
            <button type="button">Email as PDF</button>
          </div>
        </section>

        {isLoading ? <div className="do-panel">Loading DTAI summary...</div> : <ScoreCards summary={summary} doSummary={doSummary} />}
        <div className="note-text">*The variation in DQI score shown is from last available previous DTAI period*</div>

        {error && (
          <div className="do-status do-status-error">
            {error}
          </div>
        )}

        <section className="do-detail-grid">
          <AccountsTable
            rows={accounts}
            meta={accountsMeta}
            isLoading={isAccountsLoading}
            pageOffset={pageOffset}
            setPageOffset={setPageOffset}
          />
          <div className="detail-table-stack">
            {isLoading ? <div className="do-panel">Loading DTAI details...</div> : details.map((detail) => <DetailTable key={detail.title} detail={detail} />)}
          </div>
        </section>
      </main>
    </div>
  );
};

export default DtaiDqiDashboard;



